import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'zantravina_consent_v2';

function applyConsent(c) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    ad_storage: c.ads ? 'granted' : 'denied',
    ad_user_data: c.ads ? 'granted' : 'denied',
    ad_personalization: c.ads ? 'granted' : 'denied',
    analytics_storage: c.analytics ? 'granted' : 'denied',
    personalization_storage: c.personalization ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'cookie_consent_update' });
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false, personalization: false });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
      else setPrefs(JSON.parse(saved));
    } catch { setVisible(true); }

    const onOpen = () => { setVisible(true); setManage(true); };
    window.addEventListener('open-cookie-preferences', onOpen);
    return () => window.removeEventListener('open-cookie-preferences', onOpen);
  }, []);

  const persistAndClose = (c) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    applyConsent(c);
    setPrefs(c);
    setVisible(false);
    setManage(false);
  };

  const acceptAll = () => persistAndClose({ analytics: true, ads: true, personalization: true });
  const rejectAll = () => persistAndClose({ analytics: false, ads: false, personalization: false });
  const savePrefs = () => persistAndClose(prefs);

  if (!visible) return null;

  if (manage) {
    return (
      <div className="cookie-modal" role="dialog" aria-modal="true">
        <div className="cookie-panel">
          <h3 className="font-display" style={{ fontSize: 24, margin: 0 }}>{t('cookie.prefTitle')}</h3>
          <p style={{ fontSize: 14, color: 'var(--stone)', marginTop: 8 }}>{t('cookie.prefIntro')}</p>

          <div className="cookie-cat">
            <div>
              <h4>{t('cookie.catNecessaryTitle')}</h4>
              <p>{t('cookie.catNecessaryDesc')}</p>
            </div>
            <label className="switch">
              <input type="checkbox" checked disabled />
              <span className="slider" title={t('cookie.required')} />
            </label>
          </div>

          <div className="cookie-cat">
            <div>
              <h4>{t('cookie.catAnalyticsTitle')}</h4>
              <p>{t('cookie.catAnalyticsDesc')}</p>
            </div>
            <label className="switch">
              <input type="checkbox" data-testid="pref-analytics" checked={prefs.analytics} onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))} />
              <span className="slider" />
            </label>
          </div>

          <div className="cookie-cat">
            <div>
              <h4>{t('cookie.catAdsTitle')}</h4>
              <p>{t('cookie.catAdsDesc')}</p>
            </div>
            <label className="switch">
              <input type="checkbox" data-testid="pref-ads" checked={prefs.ads} onChange={(e) => setPrefs((p) => ({ ...p, ads: e.target.checked }))} />
              <span className="slider" />
            </label>
          </div>

          <div className="cookie-cat">
            <div>
              <h4>{t('cookie.catPersonalizationTitle')}</h4>
              <p>{t('cookie.catPersonalizationDesc')}</p>
            </div>
            <label className="switch">
              <input type="checkbox" data-testid="pref-personalization" checked={prefs.personalization} onChange={(e) => setPrefs((p) => ({ ...p, personalization: e.target.checked }))} />
              <span className="slider" />
            </label>
          </div>

          <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline" onClick={() => setManage(false)} data-testid="cookie-back">{t('cookie.back')}</button>
            <button className="btn btn-primary" onClick={savePrefs} data-testid="cookie-save">{t('cookie.save')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cookie-wrap">
      <div className="cookie-card" role="region" aria-label="Cookies">
        <h3>{t('cookie.title')}</h3>
        <p>{t('cookie.text')}</p>
        <div className="cookie-actions">
          <button className="btn btn-copper" onClick={acceptAll} data-testid="cookie-accept">{t('cookie.accept')}</button>
          <button className="btn btn-outline" style={{ borderColor: 'var(--paper)', color: 'var(--paper)' }} onClick={rejectAll} data-testid="cookie-reject">{t('cookie.reject')}</button>
          <button className="btn btn-ghost" style={{ color: 'var(--paper)' }} onClick={() => setManage(true)} data-testid="cookie-manage">{t('cookie.manage')}</button>
        </div>
      </div>
    </div>
  );
}
